import { message, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';

import API from 'api';
import { QUERY_KEYS } from 'api/certificates/queries';
import Card from 'components/Card';
import { ErrorContent } from 'components/FetchingErrorBoundary';
import Loading from 'components/Icons/Loading';
import ProgressSquares from 'components/ProgressSquares';
import { CertificateDetailsModel } from 'models/certificate.model';
import ComponentInformation from 'pages/CertificateDetailsPage/Other/ComponentInformation';
import GeneralInformation from 'pages/CertificateDetailsPage/Other/GeneralInformation';

import * as Styled from './DraftDetailsPage.styles';

const DraftDetailsPage = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {
    data: draft,
    isLoading,
    refetch,
  } = useQuery(
    [QUERY_KEYS.GET_DRAFTS, id],
    () => API.documents.getDocumentsID<CertificateDetailsModel>(id || ''),
    { useErrorBoundary: true }
  );
  const { mutate: publish } = useMutation(API.documents.postDocumentsIDPublicPublish, {
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEYS.GET_CERTIFICATES);
      queryClient.invalidateQueries(QUERY_KEYS.GET_DRAFTS);
      message.success(t('success.newCert'));
      setTimeout(() => navigate(`/home/certificates`), 2000);
    },
    onError: () => {
      message.error(t('error.newCert'));
    },
  });
  const { mutate: discard } = useMutation(API.documents.deleteDocumentsID, {
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEYS.GET_DRAFTS);
      message.success(t('success.discardDraft'));
      setTimeout(() => navigate(`/home/certificates`), 2000);
    },
    onError: () => {
      message.error(t('error.discardDraft'));
    },
  });

  if (isLoading) return <Loading />;
  if (!draft) return <ErrorContent onRetry={refetch} />;

  return (
    <Card backButtonLink="/home/certificates" padding="large">
      <Styled.HeaderWrapper>
        <Styled.Title>{draft.title}</Styled.Title>
        <Row justify="space-between">
          <Styled.Overall>{t('certificate.overall')}</Styled.Overall>
          <ProgressSquares progress={draft.additionalDetails?.percentRecycled ?? 0} />
        </Row>
      </Styled.HeaderWrapper>
      <GeneralInformation {...draft} />
      {draft.additionalDetails?.components?.map((component) => (
        <ComponentInformation key={component.componentId} {...component} />
      ))}
      <Styled.Divider />
      <Styled.AdditionalComments>
        {t(`certificate.additionalComments.label`)}
      </Styled.AdditionalComments>
      {draft.additionalDetails?.additionalComments}
      <Styled.ButtonRow justify="end">
        <Styled.Button
          onClick={() => {
            discard(draft.jobId);
            navigate(`/home/certificates`);
          }}
        >
          {t(`certificate.discard`)}
        </Styled.Button>
        <Styled.Button
          onClick={() => {
            publish(draft.jobId);
            navigate(`/home/certificates`);
          }}
          type="primary"
        >
          {t(`certificate.publish`)}
        </Styled.Button>
      </Styled.ButtonRow>
    </Card>
  );
};

export default DraftDetailsPage;
